import React, { useState, useEffect, useRef, DragEvent, ChangeEvent } from 'react';
import { Plus, Minus, Upload, X, Check, FileText, Image as ImageIcon } from 'lucide-react';
import { useAgreement } from '../context/AgreementContext';
import { IndividualDetails, Director } from '../types';
import { CountryAutocomplete } from './CountryAutocomplete';
import {
  getAgreementById,
  getIdFiles,
  uploadIdFile,
  deleteIdFile,
  submitPurchaserDetails,
  getPresignedUrl
} from '../services/agreementService';
import { useParams } from 'react-router-dom';

interface ContactInfoStepProps {
  onNext: () => void;
  onBack: () => void;
  agreementStatus?: string | null;
}

export function ContactInfoStep({ onNext, onBack, agreementStatus }: ContactInfoStepProps) {
  const { state, dispatch } = useAgreement();
  
  // Disable "Add Person" and "Add Director" buttons when agreement is confirmed
  const isConfirmed = agreementStatus === 'confirmed';
  const { purchaserType, individualDetails, smsfDetails, trustDetails, companyDetails } = state;
  const { agreement_id } = useParams<{ agreement_id: string }>();
  const [loading, setLoading] = useState<boolean>(false);
  const [error, setError] = useState<string | null>(null);
  const [isUpdatingFromServer, setIsUpdatingFromServer] = useState<boolean>(false);
  const [idFiles, setIdFiles] = useState<Record<string, string | {id_url: string, id_type: string}>>({});
  const [idFilesResponseData, setIdFilesResponseData] = useState<{
    files?: Record<string, any>;
    filesData?: Record<string, any>;
  }>({});
  const [uploadLoading, setUploadLoading] = useState<Record<string, boolean>>({});
  const [filePreview, setFilePreview] = useState<Record<string, { data: string, type: string, name: string }>>({});
  const [uploadErrors, setUploadErrors] = useState<Record<string, string>>({});
  const [presignedUrls, setPresignedUrls] = useState<Record<string, string>>({});
  const [presignedUrlLoading, setPresignedUrlLoading] = useState<Record<string, boolean>>({});

  const [localIndividualDetails, setLocalIndividualDetails] = useState<IndividualDetails[]>(
    purchaserType === 'personal'
      ? (individualDetails && individualDetails.length > 0 ? individualDetails : [{
          id: "1",
          firstName: '',
          middleName: '',
          lastName: '',
          email: '',
          phone: '',
          addressLine1: '',
          addressLine2: '',
          city: '',
          country: 'Australia',
          state: '',
          postcode: ''
        }])
      : []
  );

  // Update local state when context state changes
  // But only if we're not in the middle of a save operation or updating from server
  useEffect(() => {
    if (purchaserType === 'personal' && individualDetails && individualDetails.length > 0 && !loading && !isUpdatingFromServer) {
      console.log('ContactInfoStep - Updating local individual details from context:', individualDetails);
      setLocalIndividualDetails(individualDetails);
    }
  }, [purchaserType, individualDetails, loading, isUpdatingFromServer]);

  // Fetch agreement data and ID files when component mounts
  // But only if we're not in the middle of a server update
  useEffect(() => {
    const fetchAgreementData = async () => {
      if (!agreement_id || isUpdatingFromServer) return;
      
      try {
        setLoading(true);
        
        // Fetch agreement data
        const agreementData = await getAgreementById(agreement_id);
        console.log('Fetched agreement data:', agreementData);
        
        // Helper function to create person from primary_contact
        const createPersonFromPrimaryContact = (primaryContact: any) => {
          if (!primaryContact) return null;
          
          return {
            id: "1",
            firstName: primaryContact.firstname || '',
            middleName: '',
            lastName: primaryContact.lastname || '',
            email: primaryContact.email || '',
            phone: primaryContact.phone || '',
            addressLine1: '',
            addressLine2: '',
            city: '',
            country: 'Australia',
            state: '',
            postcode: ''
          };
        };
        
        // If we have purchaser details in the agreement, update the local state
        if (agreementData.purchaser_details) {
          console.log('Purchaser details:', agreementData.purchaser_details);
          
          // Handle individual details if available
          if (agreementData.purchaser_details.individualDetails?.length > 0) {
            if (purchaserType === 'personal') {
              setLocalIndividualDetails(agreementData.purchaser_details.individualDetails);
            }
          } else if (agreementData.primary_contact && purchaserType === 'personal') {
            // Auto-fill first person with primary_contact data if no existing individual details
            console.log('Auto-filling first person with primary_contact data:', agreementData.primary_contact);
            const firstPerson = createPersonFromPrimaryContact(agreementData.primary_contact);
            if (firstPerson) {
              setLocalIndividualDetails([firstPerson]);
            }
          }
          
          // Handle agreement details based on purchaser type
          if (agreementData.purchaser_details.agreementDetails) {
            const details = agreementData.purchaser_details.agreementDetails;
            
            if (purchaserType === 'smsf') {
              setLocalSMSFDetails({
                smsfName: details.smsfName || '',
                corporateTrustee: details.corporateTrustee || '',
                directors: agreementData.purchaser_details.individualDetails || []
              });
            } else if (purchaserType === 'trust') {
              setLocalTrustDetails({
                trustName: details.trustName || '',
                directors: agreementData.purchaser_details.individualDetails || []
              });
            } else if (purchaserType === 'company') {
              setLocalCompanyDetails({
                trustName: details.trustName || '',
                corporateTrustee: details.corporateTrustee || '',
                directors: agreementData.purchaser_details.individualDetails || []
              });
            }
          } else if (agreementData.primary_contact) {
            // Auto-fill first director with primary_contact data for non-personal types if no existing details
            const firstDirector = createPersonFromPrimaryContact(agreementData.primary_contact);
            if (firstDirector) {
              if (purchaserType === 'smsf') {
                console.log('Auto-filling first SMSF director with primary_contact data:', agreementData.primary_contact);
                setLocalSMSFDetails(prev => ({
                  ...prev,
                  directors: prev.directors.length === 0 ? [firstDirector] : prev.directors.map((director, index) =>
                    index === 0 ? { ...director, ...firstDirector } : director
                  )
                }));
              } else if (purchaserType === 'trust') {
                console.log('Auto-filling first trust director with primary_contact data:', agreementData.primary_contact);
                setLocalTrustDetails(prev => ({
                  ...prev,
                  directors: prev.directors.length === 0 ? [firstDirector] : prev.directors.map((director, index) =>
                    index === 0 ? { ...director, ...firstDirector } : director
                  )
                }));
              } else if (purchaserType === 'company') {
                console.log('Auto-filling first company director with primary_contact data:', agreementData.primary_contact);
                setLocalCompanyDetails(prev => ({
                  ...prev,
                  directors: prev.directors.length === 0 ? [firstDirector] : prev.directors.map((director, index) =>
                    index === 0 ? { ...director, ...firstDirector } : director
                  )
                }));
              }
            }
          }
        } else if (agreementData.primary_contact) {
          // No purchaser details exist yet, auto-fill based on purchaser type
          console.log('No existing purchaser details, auto-filling with primary_contact data:', agreementData.primary_contact);
          const firstPerson = createPersonFromPrimaryContact(agreementData.primary_contact);
          
          if (firstPerson) {
            if (purchaserType === 'personal') {
              setLocalIndividualDetails([firstPerson]);
            } else if (purchaserType === 'smsf') {
              setLocalSMSFDetails(prev => ({
                ...prev,
                directors: prev.directors.length === 0 ? [firstPerson] : prev.directors.map((director, index) =>
                  index === 0 ? { ...director, ...firstPerson } : director
                )
              }));
            } else if (purchaserType === 'trust') {
              setLocalTrustDetails(prev => ({
                ...prev,
                directors: prev.directors.length === 0 ? [firstPerson] : prev.directors.map((director, index) =>
                  index === 0 ? { ...director, ...firstPerson } : director
                )
              }));
            } else if (purchaserType === 'company') {
              setLocalCompanyDetails(prev => ({
                ...prev,
                directors: prev.directors.length === 0 ? [firstPerson] : prev.directors.map((director, index) =>
                  index === 0 ? { ...director, ...firstPerson } : director
                )
              }));
            }
          }
        }
        
        try {
          // Get the ID files response data directly from the service
          const responseData = await getIdFiles(agreement_id);
          console.log('ID files response data:', responseData);
          
          // Store the response data in state
          setIdFilesResponseData(responseData);
          
          // Also set the idFiles state for backward compatibility
          if (responseData && responseData.files) {
            setIdFiles(responseData.files);
          }
        } catch (err) {
          console.error('Error fetching ID files:', err);
        }
        
        setError(null);
      } catch (err) {
        console.error('Error fetching agreement data or ID files:', err);
        setError('Failed to load agreement data');
      } finally {
        setLoading(false);
      }
    };
    
    fetchAgreementData();
  }, [agreement_id, purchaserType, isUpdatingFromServer]);

  // Clear presigned URLs when component mounts or when agreement_id or purchaserType changes
  // This ensures fresh presigned URLs are fetched when needed
  useEffect(() => {
    console.log('ContactInfoStep - Clearing presigned URLs to prevent caching issues');
    setPresignedUrls({});
  }, [agreement_id, purchaserType]);

  // Refresh ID files when individual details change
  // This ensures ID files are properly updated when people/directors are added or removed
  // But only if we're not in the middle of a save operation or updating from server
  useEffect(() => {
    if (agreement_id && !loading && !isUpdatingFromServer) {
      console.log('ContactInfoStep - Individual details changed, refreshing ID files');
      getIdFiles(agreement_id)
        .then(responseData => {
          console.log('ID files refreshed after individual details change:', responseData);
          setIdFilesResponseData(responseData);
          if (responseData && responseData.files) {
            setIdFiles(responseData.files);
          }
        })
        .catch(err => {
          console.error('Error refreshing ID files after individual details change:', err);
        });
    }
  }, [agreement_id, individualDetails,
      purchaserType === 'smsf' ? JSON.stringify(smsfDetails?.directors) : null,
      purchaserType === 'trust' ? JSON.stringify(trustDetails?.directors) : null,
      purchaserType === 'company' ? JSON.stringify(companyDetails?.directors) : null,
      loading, isUpdatingFromServer]);

  const [localSMSFDetails, setLocalSMSFDetails] = useState({
    smsfName: smsfDetails?.smsfName || '',
    corporateTrustee: smsfDetails?.corporateTrustee || '',
    directors: smsfDetails?.directors || []
  });

  // Update local SMSF details when context state changes
  // But only if we're not in the middle of a save operation or updating from server
  useEffect(() => {
    if (purchaserType === 'smsf' && smsfDetails && !loading && !isUpdatingFromServer) {
      console.log('ContactInfoStep - Updating local SMSF details from context:', smsfDetails);
      setLocalSMSFDetails({
        smsfName: smsfDetails.smsfName || '',
        corporateTrustee: smsfDetails.corporateTrustee || '',
        directors: smsfDetails.directors || []
      });
    }
  }, [purchaserType, smsfDetails, loading, isUpdatingFromServer]);

  const [localTrustDetails, setLocalTrustDetails] = useState({
    trustName: trustDetails?.trustName || '',
    directors: trustDetails?.directors || []
  });

  // Update local Trust details when context state changes
  // But only if we're not in the middle of a save operation or updating from server
  useEffect(() => {
    if (purchaserType === 'trust' && trustDetails && !loading && !isUpdatingFromServer) {
      console.log('ContactInfoStep - Updating local Trust details from context:', trustDetails);
      setLocalTrustDetails({
        trustName: trustDetails.trustName || '',
        directors: trustDetails.directors || []
      });
    }
  }, [purchaserType, trustDetails, loading, isUpdatingFromServer]);

  const [localCompanyDetails, setLocalCompanyDetails] = useState({
    trustName: companyDetails?.trustName || '',
    corporateTrustee: companyDetails?.corporateTrustee || '',
    directors: companyDetails?.directors || []
  });

  // Update local Company details when context state changes
  // But only if we're not in the middle of a save operation or updating from server
  useEffect(() => {
    if (purchaserType === 'company' && companyDetails && !loading && !isUpdatingFromServer) {
      console.log('ContactInfoStep - Updating local Company details from context:', companyDetails);
      setLocalCompanyDetails({
        trustName: companyDetails.trustName || '',
        corporateTrustee: companyDetails.corporateTrustee || '',
        directors: companyDetails.directors || []
      });
    }
  }, [purchaserType, companyDetails, loading, isUpdatingFromServer]);

  const handleIndividualChange = (index: number, field: keyof IndividualDetails, value: string) => {
    const updated = [...localIndividualDetails];
    updated[index] = { ...updated[index], [field]: value };
    setLocalIndividualDetails(updated);
  };

  const addPerson = () => {
    const newPerson: IndividualDetails = {
      id: (localIndividualDetails.length + 1).toString(),
      firstName: '',
      middleName: '',
      lastName: '',
      email: '',
      phone: '',
      addressLine1: '',
      addressLine2: '',
      city: '',
      country: 'Australia',
      state: '',
      postcode: ''
    };
    setLocalIndividualDetails(prev => [...prev, newPerson]);
  };

  const removePerson = async (index: number) => {
    if (localIndividualDetails.length > 1) {
      // Get the person being removed to check for ID file and signature status
      const personToRemove = localIndividualDetails[index];
      
      // Check if the person has already signed
      if (personToRemove && 'has_signed' in personToRemove && personToRemove.has_signed) {
        // Show error message - cannot remove a person who has already signed
        alert("Cannot remove this person as they have already signed the agreement.");
        return;
      }
      
      // Check if the person has an ID file and a valid ID
      if (personToRemove && personToRemove.id) {
        const hasIdFile = !!idFiles[personToRemove.id];
        
        // If the person has an ID file, delete it BEFORE updating local state
        if (hasIdFile && agreement_id) {
          console.log(`ContactInfoStep - Deleting ID file for person: ${personToRemove.id}`);
          try {
            await handleRemoveFile(personToRemove.id);
            console.log(`ContactInfoStep - Successfully deleted ID file for person: ${personToRemove.id}`);
          } catch (error) {
            console.error(`ContactInfoStep - Error deleting ID file for person: ${personToRemove.id}`, error);
            // Continue with person removal even if file deletion fails
          }
        }
      }
      
      // Update local state after file deletion is complete
      setLocalIndividualDetails(prev => {
        // Filter out the person at the specified index
        const filtered = prev.filter((_, i) => i !== index);
        
        // Update IDs to maintain sequential numbering
        return filtered.map((person, i) => ({
          ...person,
          id: (i + 1).toString()
        }));
      });
    }
  };

  const addDirector = async (type: 'smsf' | 'trust' | 'company') => {
    let directorCount = 0;
    
    // Get the current count of directors based on type
    if (type === 'smsf') {
      directorCount = localSMSFDetails.directors.length;
    } else if (type === 'trust') {
      directorCount = localTrustDetails.directors.length;
    } else if (type === 'company') {
      directorCount = localCompanyDetails.directors.length;
    }
    
    // Helper function to create director from primary_contact
    const createDirectorFromPrimaryContact = (primaryContact: any) => {
      if (!primaryContact) return null;
      
      return {
        id: (directorCount + 1).toString(),
        firstName: primaryContact.firstname || '',
        middleName: '',
        lastName: primaryContact.lastname || '',
        email: primaryContact.email || '',
        phone: primaryContact.phone || '',
        addressLine1: '',
        addressLine2: '',
        city: '',
        country: 'Australia',
        state: '',
        postcode: ''
      };
    };
    
    // Create new director with sequential ID
    let newDirector: Director = {
      id: (directorCount + 1).toString(),
      firstName: '',
      middleName: '',
      lastName: '',
      email: '',
      phone: '',
      addressLine1: '',
      addressLine2: '',
      city: '',
      country: 'Australia',
      state: '',
      postcode: ''
    };
    
    // If this is the first director and we have agreement data with primary_contact, auto-fill it
    if (directorCount === 0 && agreement_id) {
      try {
        const agreementData = await getAgreementById(agreement_id);
        if (agreementData.primary_contact) {
          console.log('Auto-filling first director with primary_contact data:', agreementData.primary_contact);
          const prefilledDirector = createDirectorFromPrimaryContact(agreementData.primary_contact);
          if (prefilledDirector) {
            newDirector = prefilledDirector;
          }
        }
      } catch (err) {
        console.error('Error fetching agreement data for auto-fill:', err);
        // Continue with empty director if fetch fails
      }
    }

    if (type === 'smsf') {
      setLocalSMSFDetails(prev => ({
        ...prev,
        directors: [...prev.directors, newDirector]
      }));
    } else if (type === 'trust') {
      setLocalTrustDetails(prev => ({
        ...prev,
        directors: [...prev.directors, newDirector]
      }));
    } else if (type === 'company') {
      setLocalCompanyDetails(prev => ({
        ...prev,
        directors: [...prev.directors, newDirector]
      }));
    }
  };

  const removeDirector = (type: 'smsf' | 'trust' | 'company', directorId: string) => {
    // Find the director to check if they've already signed
    let directorToRemove: Director | undefined;
    
    if (type === 'smsf') {
      directorToRemove = localSMSFDetails.directors.find(d => d.id === directorId);
    } else if (type === 'trust') {
      directorToRemove = localTrustDetails.directors.find(d => d.id === directorId);
    } else if (type === 'company') {
      directorToRemove = localCompanyDetails.directors.find(d => d.id === directorId);
    }
    
    // Check if the director has already signed
    if (directorToRemove && 'has_signed' in directorToRemove && directorToRemove.has_signed) {
      // Show error message - cannot remove a director who has already signed
      alert("Cannot remove this director as they have already signed the agreement.");
      return;
    }
    
    if (type === 'smsf') {
      setLocalSMSFDetails(prev => ({
        ...prev,
        directors: prev.directors
          .filter((d: Director) => d.id !== directorId)
          .map((director: Director, index: number) => ({
            ...director,
            id: (index + 1).toString()
          }))
      }));
    } else if (type === 'trust') {
      setLocalTrustDetails(prev => ({
        ...prev,
        directors: prev.directors
          .filter((d: Director) => d.id !== directorId)
          .map((director: Director, index: number) => ({
            ...director,
            id: (index + 1).toString()
          }))
      }));
    } else if (type === 'company') {
      setLocalCompanyDetails(prev => ({
        ...prev,
        directors: prev.directors
          .filter((d: Director) => d.id !== directorId)
          .map((director: Director, index: number) => ({
            ...director,
            id: (index + 1).toString()
          }))
      }));
    }
  };

  const updateDirector = (type: 'smsf' | 'trust' | 'company', directorId: string, field: keyof Director, value: string) => {
    if (type === 'smsf') {
      setLocalSMSFDetails(prev => ({
        ...prev,
        directors: prev.directors.map((d: Director) =>
          d.id === directorId ? { ...d, [field]: value } : d
        )
      }));
    } else if (type === 'trust') {
      setLocalTrustDetails(prev => ({
        ...prev,
        directors: prev.directors.map((d: Director) =>
          d.id === directorId ? { ...d, [field]: value } : d
        )
      }));
    } else if (type === 'company') {
      setLocalCompanyDetails(prev => ({
        ...prev,
        directors: prev.directors.map((d: Director) =>
          d.id === directorId ? { ...d, [field]: value } : d
        )
      }));
    }
  };

  const hasIdFileForPerson = (personId: string): boolean => {
    if (!personId) return false;
    if (idFiles[personId]) return true;
    if (idFilesResponseData.files?.[personId]) return true;

    const findPerson = (): IndividualDetails | Director | undefined => {
      if (purchaserType === 'personal') {
        return localIndividualDetails.find(p => p.id === personId);
      }
      if (purchaserType === 'smsf') {
        return localSMSFDetails.directors.find(d => d.id === personId);
      }
      if (purchaserType === 'trust') {
        return localTrustDetails.directors.find(d => d.id === personId);
      }
      if (purchaserType === 'company') {
        return localCompanyDetails.directors.find(d => d.id === personId);
      }
      return undefined;
    };

    const person = findPerson();
    if (!person?.id_file) return false;
    return typeof person.id_file === 'string'
      ? !!person.id_file
      : !!person.id_file.id_url;
  };

  const getPrimaryPersonId = (): string | null => {
    if (purchaserType === 'personal') {
      return localIndividualDetails[0]?.id || null;
    }
    if (purchaserType === 'smsf') {
      return localSMSFDetails.directors[0]?.id || null;
    }
    if (purchaserType === 'trust') {
      return localTrustDetails.directors[0]?.id || null;
    }
    if (purchaserType === 'company') {
      return localCompanyDetails.directors[0]?.id || null;
    }
    return null;
  };

  const hasPrimaryIdFile = (): boolean => {
    const primaryPersonId = getPrimaryPersonId();
    return primaryPersonId ? hasIdFileForPerson(primaryPersonId) : false;
  };

  const validateForm = () => {
    let fieldsValid = false;

    if (purchaserType === 'personal') {
      fieldsValid = localIndividualDetails.every(details =>
        details.firstName && details.lastName && details.email && details.phone &&
        details.addressLine1 && details.city && details.state && details.postcode
      );
    } else if (purchaserType === 'smsf') {
      fieldsValid = !!(localSMSFDetails.smsfName && localSMSFDetails.corporateTrustee &&
             localSMSFDetails.directors.length > 0 &&
             localSMSFDetails.directors.every((d: Director) =>
               d.firstName && d.lastName && d.email && d.phone && d.addressLine1 &&
               d.city && d.state && d.postcode
             ));
    } else if (purchaserType === 'trust') {
      fieldsValid = !!(localTrustDetails.trustName && localTrustDetails.directors.length > 0 &&
             localTrustDetails.directors.every((d: Director) =>
               d.firstName && d.lastName && d.email && d.phone && d.addressLine1 &&
               d.city && d.state && d.postcode
             ));
    } else if (purchaserType === 'company') {
      fieldsValid = !!(localCompanyDetails.trustName && localCompanyDetails.corporateTrustee &&
             localCompanyDetails.directors.length > 0 &&
             localCompanyDetails.directors.every((d: Director) =>
               d.firstName && d.lastName && d.email && d.phone && d.addressLine1 &&
               d.city && d.state && d.postcode
             ));
    }

    if (!fieldsValid) return false;

    // Primary purchaser must have an uploaded identity document (optional for additional contacts)
    if (!isConfirmed && !hasPrimaryIdFile()) return false;

    return true;
  };

  const handleNext = async () => {
    if (!validateForm()) return;

    // If agreement is confirmed, just proceed without updating data
    if (isConfirmed) {
      console.log("ContactInfoStep - Agreement is confirmed, proceeding without data updates");
      onNext();
      return;
    }

    // Update party size based on number of people
    const partySize = purchaserType === 'personal' ? localIndividualDetails.length : 1;
    dispatch({ type: 'SET_PARTY_SIZE', payload: partySize });

    // First, make sure preferences are set in the context
    // This ensures preferences are included in the agreementDetails
    dispatch({ type: 'SET_PREFERENCES', payload: state.preferences });

    // Update the context state with the current local state
    // Make sure to preserve any id_file information that exists in the context
    if (purchaserType === 'personal') {
      // Merge local state with existing context state to preserve id_file information
      const mergedIndividualDetails = localIndividualDetails.map(localPerson => {
        // Find the corresponding person in the context state
        const contextPerson = individualDetails?.find(contextP => contextP.id === localPerson.id);
        
        // Merge the local person data with any id_file from context
        return {
          ...localPerson,
          // Preserve id_file if it exists in either local state or context
          ...(localPerson.id_file && { id_file: localPerson.id_file }),
          ...(contextPerson?.id_file && !localPerson.id_file && { id_file: contextPerson.id_file })
        };
      });
      
      dispatch({ type: 'SET_INDIVIDUAL_DETAILS', payload: mergedIndividualDetails });
    } else if (purchaserType === 'smsf') {
      // Merge local directors with existing context directors to preserve id_file information
      const mergedDirectors = localSMSFDetails.directors.map((localDirector: Director) => {
        // Find the corresponding director in the context state
        const contextDirector = state.smsfDetails?.directors?.find(contextD => contextD.id === localDirector.id);
        
        // Merge the local director data with any id_file from context
        return {
          ...localDirector,
          // Preserve id_file if it exists in either local state or context
          ...(localDirector.id_file && { id_file: localDirector.id_file }),
          ...(contextDirector?.id_file && !localDirector.id_file && { id_file: contextDirector.id_file })
        };
      });
      
      // Set SMSF details with merged directors in one action
      // This avoids the problematic clear-and-add pattern that triggers file deletions
      dispatch({ type: 'SET_SMSF_DETAILS_WITH_DIRECTORS', payload: {
        smsfName: localSMSFDetails.smsfName,
        corporateTrustee: localSMSFDetails.corporateTrustee,
        directors: mergedDirectors
      }});
    } else if (purchaserType === 'trust') {
      // Merge local directors with existing context directors to preserve id_file information
      const mergedDirectors = localTrustDetails.directors.map((localDirector: Director) => {
        // Find the corresponding director in the context state
        const contextDirector = state.trustDetails?.directors?.find(contextD => contextD.id === localDirector.id);
        
        // Merge the local director data with any id_file from context
        return {
          ...localDirector,
          // Preserve id_file if it exists in either local state or context
          ...(localDirector.id_file && { id_file: localDirector.id_file }),
          ...(contextDirector?.id_file && !localDirector.id_file && { id_file: contextDirector.id_file })
        };
      });
      
      // Set trust details with merged directors in one action
      // This avoids the problematic clear-and-add pattern that triggers file deletions
      dispatch({ type: 'SET_TRUST_DETAILS_WITH_DIRECTORS', payload: {
        trustName: localTrustDetails.trustName,
        corporateTrustee: '',
        directors: mergedDirectors
      }});
    } else if (purchaserType === 'company') {
      // Merge local directors with existing context directors to preserve id_file information
      const mergedDirectors = localCompanyDetails.directors.map((localDirector: Director) => {
        // Find the corresponding director in the context state
        const contextDirector = state.companyDetails?.directors?.find(contextD => contextD.id === localDirector.id);
        
        // Merge the local director data with any id_file from context
        return {
          ...localDirector,
          // Preserve id_file if it exists in either local state or context
          ...(localDirector.id_file && { id_file: localDirector.id_file }),
          ...(contextDirector?.id_file && !localDirector.id_file && { id_file: contextDirector.id_file })
        };
      });
      
      // Set company details with merged directors in one action
      // This avoids the problematic clear-and-add pattern that triggers file deletions
      dispatch({ type: 'SET_COMPANY_DETAILS_WITH_DIRECTORS', payload: {
        trustName: localCompanyDetails.trustName,
        corporateTrustee: localCompanyDetails.corporateTrustee,
        directors: mergedDirectors
      }});
    }

    try {
      // Save the current state to ensure we don't lose any data
      // This is especially important for preserving id_file information and agreementDetails
      const saveSuccess = await savePurchaserDetails();
      
      if (saveSuccess) {
        // Only proceed to the next step if the save was successful
        // This prevents the AgreementWizard from overwriting our data
        onNext();
      } else {
        setError('Failed to save contact details. Please try again.');
      }
    } catch (err) {
      console.error('Error in handleNext:', err);
      setError('An error occurred while saving your data. Please try again.');
    }
  };

  const renderPersonForm = (details: IndividualDetails, index: number) => (
    <div key={index} className="p-6 bg-gray-50 rounded-lg mb-6">
      <div className="flex items-center justify-between mb-6">
        <h4 className="text-lg font-semibold text-gray-900">
          {localIndividualDetails.length > 1 ? `Person ${index + 1}` : 'Contact Information'}
        </h4>
        {localIndividualDetails.length > 1 && (
          'has_signed' in details && details.has_signed ? (
            <div className="flex items-center gap-2 px-3 py-2 text-green-600 rounded-lg text-sm">
              <Check className="w-4 h-4" />
              Signed
            </div>
          ) : (
            <button
              type="button"
              onClick={() => removePerson(index)}
              className="flex items-center gap-2 px-3 py-2 text-red-600 hover:bg-red-50 rounded-lg text-sm transition-colors"
            >
              <Minus className="w-4 h-4" />
              Remove
            </button>
          )
        )}
      </div>
      
      {/* Name Fields */}
      <div className="grid grid-cols-1 sm:grid-cols-3 gap-4 mb-6">
        <div>
          <label className="block text-sm font-medium text-gray-700 mb-2">
            First Name *
          </label>
          <input
            type="text"
            value={details.firstName}
            onChange={(e) => handleIndividualChange(index, 'firstName', e.target.value)}
            className={`w-full px-4 py-3 border border-gray-300 rounded-lg focus:outline-none focus:ring-2 focus:ring-primary-500 min-h-[48px] ${
              isConfirmed ? 'bg-gray-100 cursor-not-allowed' : ''
            }`}
            placeholder="First name"
            disabled={isConfirmed}
            readOnly={isConfirmed}
          />
        </div>
        
        <div>
          <label className="block text-sm font-medium text-gray-700 mb-2">
            Middle Name
          </label>
          <input
            type="text"
            value={details.middleName || ''}
            onChange={(e) => handleIndividualChange(index, 'middleName', e.target.value)}
            className="w-full px-4 py-3 border border-gray-300 rounded-lg focus:outline-none focus:ring-2 focus:ring-primary-500 min-h-[48px]"
            placeholder="Middle name (optional)"
          />
        </div>
        
        <div>
          <label className="block text-sm font-medium text-gray-700 mb-2">
            Last Name *
          </label>
          <input
            type="text"
            value={details.lastName}
            onChange={(e) => handleIndividualChange(index, 'lastName', e.target.value)}
            className="w-full px-4 py-3 border border-gray-300 rounded-lg focus:outline-none focus:ring-2 focus:ring-primary-500 min-h-[48px]"
            placeholder="Last name"
          />
        </div>
      </div>

      {/* Contact Fields */}
      <div className="grid grid-cols-1 sm:grid-cols-2 gap-4 mb-6">
        <div>
          <label className="block text-sm font-medium text-gray-700 mb-2">
            Email Address *
          </label>
          <input
            type="email"
            value={details.email}
            onChange={(e) => handleIndividualChange(index, 'email', e.target.value)}
            className={`w-full px-4 py-3 border border-gray-300 rounded-lg focus:outline-none focus:ring-2 focus:ring-primary-500 min-h-[48px] ${
              isConfirmed ? 'bg-gray-100 cursor-not-allowed' : ''
            }`}
            placeholder="Enter email address"
            disabled={isConfirmed}
            readOnly={isConfirmed}
          />
        </div>
        
        <div>
          <label className="block text-sm font-medium text-gray-700 mb-2">
            Phone Number *
          </label>
          <input
            type="tel"
            value={details.phone}
            onChange={(e) => handleIndividualChange(index, 'phone', e.target.value)}
            className="w-full px-4 py-3 border border-gray-300 rounded-lg focus:outline-none focus:ring-2 focus:ring-primary-500 min-h-[48px]"
            placeholder="Enter phone number"
          />
        </div>
      </div>

      {/* Address Fields */}
      <div className="space-y-4 mb-6">
        <div>
          <label className="block text-sm font-medium text-gray-700 mb-2">
            Address Line 1 *
          </label>
          <input
            type="text"
            value={details.addressLine1}
            onChange={(e) => handleIndividualChange(index, 'addressLine1', e.target.value)}
            className="w-full px-4 py-3 border border-gray-300 rounded-lg focus:outline-none focus:ring-2 focus:ring-primary-500 min-h-[48px]"
            placeholder="Enter street address"
          />
        </div>
        
        <div className="grid grid-cols-1 sm:grid-cols-3 gap-4">
          <div className="sm:col-span-2">
            <label className="block text-sm font-medium text-gray-700 mb-2">
              Address Line 2
            </label>
            <input
              type="text"
              value={details.addressLine2 || ''}
              onChange={(e) => handleIndividualChange(index, 'addressLine2', e.target.value)}
              className="w-full px-4 py-3 border border-gray-300 rounded-lg focus:outline-none focus:ring-2 focus:ring-primary-500 min-h-[48px]"
              placeholder="Apartment, suite, etc. (optional)"
            />
          </div>
          
          <div>
            <label className="block text-sm font-medium text-gray-700 mb-2">
              City *
            </label>
            <input
              type="text"
              value={details.city}
              onChange={(e) => handleIndividualChange(index, 'city', e.target.value)}
              className="w-full px-4 py-3 border border-gray-300 rounded-lg focus:outline-none focus:ring-2 focus:ring-primary-500 min-h-[48px]"
              placeholder="Enter city"
            />
          </div>
        </div>
        
        <div className="grid grid-cols-1 sm:grid-cols-3 gap-4">
          
          <div>
            <label className="block text-sm font-medium text-gray-700 mb-2">
              State *
            </label>
            <input
              type="text"
              value={details.state}
              onChange={(e) => handleIndividualChange(index, 'state', e.target.value)}
              className="w-full px-4 py-3 border border-gray-300 rounded-lg focus:outline-none focus:ring-2 focus:ring-primary-500 min-h-[48px]"
              placeholder="State"
            />
          </div>
          
          <div>
            <label className="block text-sm font-medium text-gray-700 mb-2">
              Postcode *
            </label>
            <input
              type="text"
              value={details.postcode}
              onChange={(e) => handleIndividualChange(index, 'postcode', e.target.value)}
              className="w-full px-4 py-3 border border-gray-300 rounded-lg focus:outline-none focus:ring-2 focus:ring-primary-500 min-h-[48px]"
              placeholder="Postcode"
            />
          </div>
          
          <div>
            <label className="block text-sm font-medium text-gray-700 mb-2">
              Country *
            </label>
            <CountryAutocomplete
              value={details.country}
              onChange={(value) => handleIndividualChange(index, 'country', value)}
              placeholder="Type to search countries..."
            />
          </div>
        </div>
      </div>

      {/* Photo ID Upload */}
      <div>
        <label className="block text-sm font-medium text-gray-700 mb-2">
          Photo ID{index === 0 ? ' *' : ''}
        </label>
        <FileUploadArea
          personId={details.id || ''}
          isRequired={index === 0}
          isUploaded={hasIdFileForPerson(details.id || '')}
          uploadedFileName={
            idFiles[details.id || '']
              ? typeof idFiles[details.id || ''] === 'string'
                ? idFiles[details.id || ''] as string
                : (idFiles[details.id || ''] as {id_url: string}).id_url
              : ''
          }
          filePreview={filePreview[details.id || '']}
          isLoading={uploadLoading[details.id || '']}
          error={uploadErrors[details.id || '']}
          disabled={isConfirmed}
          onFileSelect={(file) => handleFileSelect(file, details.id || '')}
          onUpload={() => handleUploadFile(details.id || '')}
          onRemove={() => handleRemoveFile(details.id || '')}
          onCancelPreview={() => handleCancelPreview(details.id || '')}
        />
      </div>
    </div>
  );

  const renderDirectorForm = (director: Director, type: 'smsf' | 'trust' | 'company', index: number) => (
    <div key={director.id} className="p-6 bg-gray-50 rounded-lg mb-6">
      <div className="flex items-center justify-between mb-6">
        <h4 className="text-lg font-semibold text-gray-900">
          Director {index + 1}
        </h4>
        {'has_signed' in director && director.has_signed ? (
          <div className="flex items-center gap-2 px-3 py-2 text-green-600 rounded-lg text-sm">
            <Check className="w-4 h-4" />
            Signed
          </div>
        ) : (
          <button
            type="button"
            onClick={() => removeDirector(type, director.id)}
            className="flex items-center gap-2 px-3 py-2 text-red-600 hover:bg-red-50 rounded-lg text-sm transition-colors"
          >
            <Minus className="w-4 h-4" />
            Remove
          </button>
        )}
      </div>
      
      {/* Name Fields */}
      <div className="grid grid-cols-1 sm:grid-cols-3 gap-4 mb-6">
        <div>
          <label className="block text-sm font-medium text-gray-700 mb-2">
            First Name *
          </label>
          <input
            type="text"
            value={director.firstName}
            onChange={(e) => updateDirector(type, director.id, 'firstName', e.target.value)}
            className="w-full px-4 py-3 border border-gray-300 rounded-lg focus:outline-none focus:ring-2 focus:ring-primary-500 min-h-[48px]"
            placeholder="First name"
          />
        </div>
        
        <div>
          <label className="block text-sm font-medium text-gray-700 mb-2">
            Middle Name
          </label>
          <input
            type="text"
            value={director.middleName || ''}
            onChange={(e) => updateDirector(type, director.id, 'middleName', e.target.value)}
            className="w-full px-4 py-3 border border-gray-300 rounded-lg focus:outline-none focus:ring-2 focus:ring-primary-500 min-h-[48px]"
            placeholder="Middle name (optional)"
          />
        </div>
        
        <div>
          <label className="block text-sm font-medium text-gray-700 mb-2">
            Last Name *
          </label>
          <input
            type="text"
            value={director.lastName}
            onChange={(e) => updateDirector(type, director.id, 'lastName', e.target.value)}
            className="w-full px-4 py-3 border border-gray-300 rounded-lg focus:outline-none focus:ring-2 focus:ring-primary-500 min-h-[48px]"
            placeholder="Last name"
          />
        </div>
      </div>

      {/* Contact Fields */}
      <div className="grid grid-cols-1 sm:grid-cols-2 gap-4 mb-6">
        <div>
          <label className="block text-sm font-medium text-gray-700 mb-2">
            Email Address *
          </label>
          <input
            type="email"
            value={director.email}
            onChange={(e) => updateDirector(type, director.id, 'email', e.target.value)}
            className="w-full px-4 py-3 border border-gray-300 rounded-lg focus:outline-none focus:ring-2 focus:ring-primary-500 min-h-[48px]"
            placeholder="Enter email address"
          />
        </div>
        
        <div>
          <label className="block text-sm font-medium text-gray-700 mb-2">
            Phone Number *
          </label>
          <input
            type="tel"
            value={director.phone}
            onChange={(e) => updateDirector(type, director.id, 'phone', e.target.value)}
            className="w-full px-4 py-3 border border-gray-300 rounded-lg focus:outline-none focus:ring-2 focus:ring-primary-500 min-h-[48px]"
            placeholder="Enter phone number"
          />
        </div>
      </div>

      {/* Address Fields */}
      <div className="space-y-4 mb-6">
        <div>
          <label className="block text-sm font-medium text-gray-700 mb-2">
            Address Line 1 *
          </label>
          <input
            type="text"
            value={director.addressLine1}
            onChange={(e) => updateDirector(type, director.id, 'addressLine1', e.target.value)}
            className="w-full px-4 py-3 border border-gray-300 rounded-lg focus:outline-none focus:ring-2 focus:ring-primary-500 min-h-[48px]"
            placeholder="Enter street address"
          />
        </div>
        
        <div className="grid grid-cols-1 sm:grid-cols-3 gap-4">
          <div className="sm:col-span-2">
            <label className="block text-sm font-medium text-gray-700 mb-2">
              Address Line 2
            </label>
            <input
              type="text"
              value={director.addressLine2 || ''}
              onChange={(e) => updateDirector(type, director.id, 'addressLine2', e.target.value)}
              className="w-full px-4 py-3 border border-gray-300 rounded-lg focus:outline-none focus:ring-2 focus:ring-primary-500 min-h-[48px]"
              placeholder="Apartment, suite, etc. (optional)"
            />
          </div>
          
          <div>
            <label className="block text-sm font-medium text-gray-700 mb-2">
              City *
            </label>
            <input
              type="text"
              value={director.city}
              onChange={(e) => updateDirector(type, director.id, 'city', e.target.value)}
              className="w-full px-4 py-3 border border-gray-300 rounded-lg focus:outline-none focus:ring-2 focus:ring-primary-500 min-h-[48px]"
              placeholder="Enter city"
            />
          </div>
        </div>
        
        <div className="grid grid-cols-1 sm:grid-cols-3 gap-4">
          
          <div>
            <label className="block text-sm font-medium text-gray-700 mb-2">
              State *
            </label>
            <input
              type="text"
              value={director.state}
              onChange={(e) => updateDirector(type, director.id, 'state', e.target.value)}
              className="w-full px-4 py-3 border border-gray-300 rounded-lg focus:outline-none focus:ring-2 focus:ring-primary-500 min-h-[48px]"
              placeholder="State"
            />
          </div>
          
          <div>
            <label className="block text-sm font-medium text-gray-700 mb-2">
              Postcode *
            </label>
            <input
              type="text"
              value={director.postcode}
              onChange={(e) => updateDirector(type, director.id, 'postcode', e.target.value)}
              className="w-full px-4 py-3 border border-gray-300 rounded-lg focus:outline-none focus:ring-2 focus:ring-primary-500 min-h-[48px]"
              placeholder="Postcode"
            />
          </div>
          
          <div>
            <label className="block text-sm font-medium text-gray-700 mb-2">
              Country *
            </label>
            <CountryAutocomplete
              value={director.country}
              onChange={(value) => updateDirector(type, director.id, 'country', value)}
              placeholder="Type to search countries..."
            />
          </div>
        </div>
      </div>

      {/* Photo ID Upload */}
      <div>
        <label className="block text-sm font-medium text-gray-700 mb-2">
          Photo ID{index === 0 ? ' *' : ''}
        </label>
        <FileUploadArea
          personId={director.id}
          isRequired={index === 0}
          isUploaded={hasIdFileForPerson(director.id)}
          uploadedFileName={
            idFiles[director.id]
              ? typeof idFiles[director.id] === 'string'
                ? idFiles[director.id] as string
                : (idFiles[director.id] as {id_url: string}).id_url
              : ''
          }
          filePreview={filePreview[director.id]}
          isLoading={uploadLoading[director.id]}
          error={uploadErrors[director.id]}
          disabled={isConfirmed}
          onFileSelect={(file) => handleFileSelect(file, director.id)}
          onUpload={() => handleUploadFile(director.id)}
          onRemove={() => handleRemoveFile(director.id)}
          onCancelPreview={() => handleCancelPreview(director.id)}
        />
      </div>
    </div>
  );

  // File handling functions
  const handleFileSelect = (file: File, personId: string) => {
    if (!file) return;
    
    // Check file type
    const validTypes = ['image/jpeg', 'image/jpg', 'image/png', 'application/pdf'];
    if (!validTypes.includes(file.type)) {
      // Set error on the specific upload card instead of global error
      setUploadErrors(prev => ({
        ...prev,
        [personId]: 'Please select a valid file type (JPG, PNG, or PDF)'
      }));
      return;
    }
    
    // Check file size with buffer (9.5MB max instead of 10MB to provide a safety margin)
    const fileSizeMB = file.size / (1024 * 1024);
    const maxSizeMB = 10;
    const bufferSizeMB = 9.5; // Buffer to prevent boundary issues
    const bufferSizeBytes = bufferSizeMB * 1024 * 1024;
    
    console.log(`Frontend - File size check: ${file.name}`);
    console.log(`Frontend - File size: ${fileSizeMB.toFixed(2)}MB (${file.size} bytes)`);
    console.log(`Frontend - Max allowed size: ${maxSizeMB}MB`);
    console.log(`Frontend - Buffer size limit: ${bufferSizeMB}MB (${bufferSizeBytes} bytes)`);
    
    if (file.size > bufferSizeBytes) {
      console.log(`Frontend - File size exceeds buffer limit: ${fileSizeMB.toFixed(2)}MB > ${bufferSizeMB}MB`);
      // Set error on the specific upload card instead of global error
      setUploadErrors(prev => ({
        ...prev,
        [personId]: `File size (${fileSizeMB.toFixed(2)}MB) must be less than ${bufferSizeMB}MB to ensure successful upload`
      }));
      return;
    } else {
      console.log(`Frontend - File size within buffer limit: ${fileSizeMB.toFixed(2)}MB <= ${bufferSizeMB}MB`);
    }
    
    // Clear any previous errors
    setError(null);
    
    // Clear any specific upload error for this person
    setUploadErrors(prev => {
      const newState = { ...prev };
      delete newState[personId];
      return newState;
    });
    
    // Read the file and create a preview
    const reader = new FileReader();
    reader.onload = (e) => {
      if (e.target?.result) {
        const fileData = e.target.result as string;
        setFilePreview(prev => ({
          ...prev,
          [personId]: {
            data: fileData,
            type: file.type,
            name: file.name
          }
        }));
      }
    };
    reader.readAsDataURL(file);
  };
  
  // Save purchaser details to the database
  const savePurchaserDetails = async () => {
    if (!agreement_id) return false;
    
    try {
      setLoading(true);
      
      // Prepare purchaser details with the new consolidated structure
      let agreementDetails = {};
      let individualDetails: (IndividualDetails | Director)[] = [];
      
      // Set agreement details based on purchaser type
      if (purchaserType === 'personal') {
        // For personal, there are no specific agreement details
        // But we still need to include preferences
        agreementDetails = {
          // Include preferences from the global context state
          preferences: state.preferences
        };
        individualDetails = localIndividualDetails;
      } else if (purchaserType === 'smsf') {
        agreementDetails = {
          smsfName: localSMSFDetails.smsfName,
          corporateTrustee: localSMSFDetails.corporateTrustee,
          // Include preferences from the global context state
          preferences: state.preferences
        };
        individualDetails = localSMSFDetails.directors;
      } else if (purchaserType === 'trust') {
        agreementDetails = {
          trustName: localTrustDetails.trustName,
          // Include preferences from the global context state
          preferences: state.preferences
        };
        individualDetails = localTrustDetails.directors;
      } else if (purchaserType === 'company') {
        agreementDetails = {
          trustName: localCompanyDetails.trustName,
          corporateTrustee: localCompanyDetails.corporateTrustee,
          // Include preferences from the global context state
          preferences: state.preferences
        };
        individualDetails = localCompanyDetails.directors;
      }
      
      // Create the consolidated purchaser details structure
      const purchaserDetails = {
        purchaserType: purchaserType,
        purchaserInfo: {
          // This would typically come from another part of the application
          // For now, we'll leave it as an empty object
        },
        agreementDetails: agreementDetails,
        individualDetails: individualDetails
      };
      
      console.log('Submitting consolidated purchaser details:', purchaserDetails);
      
      // Use the imported submitPurchaserDetails function instead of direct fetch
      const result = await submitPurchaserDetails(agreement_id, purchaserDetails);
      console.log('Saved purchaser details:', result);
      
      // Update the frontend context with the server response
      // This ensures the context reflects the current backend state
      if (result && result.agreement) {
        console.log('Updating context with server response:', result.agreement);
        
        // Set flag to prevent useEffect hooks from interfering
        setIsUpdatingFromServer(true);
        
        // Update the context with the complete agreement data from the server
        dispatch({ type: 'LOAD_AGREEMENT', payload: result.agreement });
        
        // Also update the local states to match the server response
        if (result.agreement.purchaser_details) {
          const serverPurchaserDetails = result.agreement.purchaser_details;
          
          if (purchaserType === 'personal' && serverPurchaserDetails.individualDetails) {
            setLocalIndividualDetails(serverPurchaserDetails.individualDetails);
          } else if (purchaserType === 'smsf' && serverPurchaserDetails.agreementDetails && serverPurchaserDetails.individualDetails) {
            setLocalSMSFDetails({
              smsfName: serverPurchaserDetails.agreementDetails.smsfName || '',
              corporateTrustee: serverPurchaserDetails.agreementDetails.corporateTrustee || '',
              directors: serverPurchaserDetails.individualDetails || []
            });
          } else if (purchaserType === 'trust' && serverPurchaserDetails.agreementDetails && serverPurchaserDetails.individualDetails) {
            setLocalTrustDetails({
              trustName: serverPurchaserDetails.agreementDetails.trustName || '',
              directors: serverPurchaserDetails.individualDetails || []
            });
          } else if (purchaserType === 'company' && serverPurchaserDetails.agreementDetails && serverPurchaserDetails.individualDetails) {
            setLocalCompanyDetails({
              trustName: serverPurchaserDetails.agreementDetails.trustName || '',
              corporateTrustee: serverPurchaserDetails.agreementDetails.corporateTrustee || '',
              directors: serverPurchaserDetails.individualDetails || []
            });
          }
        }
        
        // Clear the flag after a short delay to allow state updates to complete
        setTimeout(() => {
          setIsUpdatingFromServer(false);
        }, 100);
      }
      
      return true;
    } catch (err) {
      console.error('Error saving purchaser details:', err);
      setError('Failed to save purchaser details. Please try again.');
      return false;
    } finally {
      setLoading(false);
    }
  };

  /**
   * Handles the file upload process with the following steps:
   * 1. Validates the form to ensure all required fields are filled
   * 2. Saves the purchaser details to the database to ensure the individual exists
   * 3. Adds a delay to allow the database to update
   * 4. Attempts to upload the file with a retry mechanism using exponential backoff
   * 5. Shows appropriate success/error messages based on the result
   *
   * This approach solves the "Individual not found" 404 error by ensuring the
   * individual record exists in the database before attempting to upload the file.
   */
  const handleUploadFile = async (personId: string) => {
    if (!agreement_id || !personId || !filePreview[personId]) return;
    
    try {
      // Check if this specific person/director has valid fields
      const isPersonValid = () => {
        if (purchaserType === 'personal') {
          const person = localIndividualDetails.find(p => p.id === personId);
          return person && person.firstName && person.lastName && person.email && person.phone &&
                 person.addressLine1 && person.city && person.state && person.postcode;
        } else if (purchaserType === 'smsf') {
          const director = localSMSFDetails.directors.find(d => d.id === personId);
          return director && director.firstName && director.lastName && director.email && director.phone &&
                 director.addressLine1 && director.city && director.state && director.postcode;
        } else if (purchaserType === 'trust') {
          const director = localTrustDetails.directors.find(d => d.id === personId);
          return director && director.firstName && director.lastName && director.email && director.phone &&
                 director.addressLine1 && director.city && director.state && director.postcode;
        } else if (purchaserType === 'company') {
          const director = localCompanyDetails.directors.find(d => d.id === personId);
          return director && director.firstName && director.lastName && director.email && director.phone &&
                 director.addressLine1 && director.city && director.state && director.postcode;
        }
        return false;
      };
      
      // First check if this person's fields are valid before proceeding
      if (!isPersonValid()) {
        // Set error on the specific upload card instead of global error
        setUploadErrors(prev => ({
          ...prev,
          [personId]: 'Please fill out all required fields for this person before uploading ID files.'
        }));
        return;
      }
      
      // Clear any previous errors for this person
      setUploadErrors(prev => {
        const newState = { ...prev };
        delete newState[personId];
        return newState;
      });
      
      // Set loading state
      setUploadLoading(prev => ({ ...prev, [personId]: true }));
      
      // Check if the person already has an ID file
      const hasExistingFile = !!idFiles[personId];
      
      // If they already have a file, show a confirmation dialog
      if (hasExistingFile) {
        const confirmReplace = window.confirm(
          'This person already has an ID file. Do you want to replace it with the new file?'
        );
        
        if (!confirmReplace) {
          setUploadLoading(prev => ({ ...prev, [personId]: false }));
          // Clear the preview
          setFilePreview(prev => {
            const newState = { ...prev };
            delete newState[personId];
            return newState;
          });
          return;
        }
      }
      
      // Save purchaser details in the background without triggering component refresh
      console.log('Saving purchaser details in background before uploading file...');
      
      // Create a background save function that doesn't update the context
      const backgroundSave = async () => {
        try {
          // Prepare purchaser details with the new consolidated structure
          let agreementDetails = {};
          let individualDetails: (IndividualDetails | Director)[] = [];
          
          // Set agreement details based on purchaser type
          if (purchaserType === 'personal') {
            agreementDetails = { preferences: state.preferences };
            individualDetails = localIndividualDetails;
          } else if (purchaserType === 'smsf') {
            agreementDetails = {
              smsfName: localSMSFDetails.smsfName,
              corporateTrustee: localSMSFDetails.corporateTrustee,
              preferences: state.preferences
            };
            individualDetails = localSMSFDetails.directors;
          } else if (purchaserType === 'trust') {
            agreementDetails = {
              trustName: localTrustDetails.trustName,
              preferences: state.preferences
            };
            individualDetails = localTrustDetails.directors;
          } else if (purchaserType === 'company') {
            agreementDetails = {
              trustName: localCompanyDetails.trustName,
              corporateTrustee: localCompanyDetails.corporateTrustee,
              preferences: state.preferences
            };
            individualDetails = localCompanyDetails.directors;
          }
          
          // Create the consolidated purchaser details structure
          const purchaserDetails = {
            purchaserType: purchaserType,
            purchaserInfo: {},
            agreementDetails: agreementDetails,
            individualDetails: individualDetails
          };
          
          console.log('Background saving purchaser details:', purchaserDetails);
          
          // Save without updating context
          await submitPurchaserDetails(agreement_id, purchaserDetails);
          console.log('Background save completed successfully');
          
          return true;
        } catch (err) {
          console.error('Background save failed:', err);
          return false;
        }
      };
      
      const saveSuccess = await backgroundSave();
      
      if (!saveSuccess) {
        throw new Error('Failed to save purchaser details before uploading file');
      }
      
      console.log('Purchaser details saved successfully, waiting for database update...');
      
      // Add a delay to ensure the database has been updated
      console.log('Waiting 1 second for database to update...');
      await new Promise(resolve => setTimeout(resolve, 1000));
      
      // Log file details before upload
      const fileData = filePreview[personId].data;
      const fileType = filePreview[personId].type;
      const fileName = filePreview[personId].name;
      
      // Calculate approximate size of base64 data
      let base64Size = 0;
      let base64Data = '';
      if (fileData.includes(',')) {
        base64Data = fileData.split(',')[1];
        base64Size = base64Data.length;
      } else {
        base64Data = fileData;
        base64Size = fileData.length;
      }
      
      // Estimate original file size (4 base64 chars represent 3 bytes)
      const estimatedSizeBytes = Math.floor((base64Size * 3) / 4);
      const estimatedSizeMB = estimatedSizeBytes / (1024 * 1024);
      const maxSizeMB = 10;
      const maxSizeBytes = maxSizeMB * 1024 * 1024;
      
      // Calculate base64 overhead
      const originalFileSize = filePreview[personId].data ?
        new Blob([filePreview[personId].data]).size : 0;
      const base64Overhead = base64Size - originalFileSize;
      const base64OverheadPercent = originalFileSize ?
        ((base64Size - originalFileSize) / originalFileSize) * 100 : 0;
      
      console.log(`Frontend - Uploading file: ${fileName}, Type: ${fileType}`);
      console.log(`Frontend - Original file size: ${(originalFileSize / (1024 * 1024)).toFixed(2)}MB (${originalFileSize} bytes)`);
      console.log(`Frontend - Base64 data length: ${base64Size} characters`);
      console.log(`Frontend - Estimated file size from base64: ${estimatedSizeMB.toFixed(2)}MB (${estimatedSizeBytes} bytes)`);
      console.log(`Frontend - Base64 overhead: ${(base64Overhead / 1024).toFixed(2)}KB (${base64OverheadPercent.toFixed(2)}%)`);
      console.log(`Frontend - Max allowed size: ${maxSizeMB}MB (${maxSizeBytes} bytes)`);
      
      if (estimatedSizeBytes > maxSizeBytes) {
        console.log(`Frontend - Estimated file size exceeds limit: ${estimatedSizeMB.toFixed(2)}MB > ${maxSizeMB}MB`);
        setUploadErrors(prev => ({
          ...prev,
          [personId]: `File size (${estimatedSizeMB.toFixed(2)}MB) exceeds the maximum allowed limit of ${maxSizeMB}MB`
        }));
        throw new Error(`File size (${estimatedSizeMB.toFixed(2)}MB) exceeds the maximum allowed limit of ${maxSizeMB}MB`);
      } else {
        console.log(`Frontend - Estimated file size within limit: ${estimatedSizeMB.toFixed(2)}MB <= ${maxSizeMB}MB`);
      }
      
      console.log('Frontend - Now uploading file...');
      
      // Upload the file - no retry mechanism, just one attempt
      try {
        const result = await uploadIdFile(
          agreement_id,
          personId,
          filePreview[personId].data
        );
        
        console.log('File upload result:', result);
        
        // Only update the ID file display - don't refresh the entire component
        if (result && result.id_file) {
          // Update the idFiles state with the new file (for the upload area display)
          setIdFiles(prev => ({
            ...prev,
            [personId]: {
              id_url: result.id_file.id_url,
              id_type: result.id_file.id_type
            }
          }));
          
          // Update the idFilesResponseData state with the new file data
          setIdFilesResponseData(prev => {
            // Create a new object with the updated files
            const updatedFiles = { ...(prev.files || {}) };
            updatedFiles[personId] = {
              id_url: result.id_file.id_url,
              id_type: result.id_file.id_type
            };
            
            // Create a new object with the updated filesData
            const updatedFilesData = { ...(prev.filesData || {}) };
            updatedFilesData[personId] = {
              name: result.name || '',
              role: 'individual',
              url: result.url || '',
              id_file: result.id_file
            };
            
            return {
              files: updatedFiles,
              filesData: updatedFilesData
            };
          });
          
          // Clear the presigned URL from state to force a fresh fetch when viewing the thumbnail
          setPresignedUrls(prev => {
            const newState = { ...prev };
            delete newState[personId];
            return newState;
          });
          
          // Update only the local state with the id_file information
          // Don't update the global context to avoid triggering component refresh
          if (purchaserType === 'personal') {
            setLocalIndividualDetails(prev =>
              prev.map(person =>
                person.id === personId ? { ...person, id_file: result.id_file } : person
              )
            );
          } else if (purchaserType === 'smsf') {
            setLocalSMSFDetails(prev => ({
              ...prev,
              directors: prev.directors.map(director =>
                director.id === personId ? { ...director, id_file: result.id_file } : director
              )
            }));
          } else if (purchaserType === 'trust') {
            setLocalTrustDetails(prev => ({
              ...prev,
              directors: prev.directors.map(director =>
                director.id === personId ? { ...director, id_file: result.id_file } : director
              )
            }));
          } else if (purchaserType === 'company') {
            setLocalCompanyDetails(prev => ({
              ...prev,
              directors: prev.directors.map(director =>
                director.id === personId ? { ...director, id_file: result.id_file } : director
              )
            }));
          }
        }
        
        // Clear the preview
        setFilePreview(prev => {
          const newState = { ...prev };
          delete newState[personId];
          return newState;
        });
        
        // Show success message
        const successElement = document.createElement('div');
        successElement.className = 'fixed bottom-4 right-4 bg-green-100 border border-green-400 text-green-700 px-4 py-3 rounded z-50';
        successElement.innerHTML = '<strong>Success!</strong> ID file uploaded successfully.';
        document.body.appendChild(successElement);
        
        // Remove the success message after 3 seconds
        setTimeout(() => {
          if (document.body.contains(successElement)) {
            document.body.removeChild(successElement);
          }
        }, 3000);
        
        console.log('Upload completed successfully - ID file display updated');
      } catch (uploadError) {
        console.error('Error in file upload:', uploadError);
        throw uploadError; // Re-throw to be caught by the outer catch block
      }
    } catch (err) {
      console.error('Error uploading file:', err);
      
      // Provide more specific error messages based on the error
      let errorMessage = 'Failed to upload file.';
      
      if (err instanceof Error) {
        if (err.message.includes('Failed to save purchaser details')) {
          errorMessage = 'Failed to save contact details before uploading. Please ensure all required fields are filled.';
        } else if (err.message.includes('Individual not found')) {
          errorMessage = 'Individual not found. Please ensure all details are saved before uploading.';
        } else if (err.message.includes('404')) {
          errorMessage = 'Server could not find the individual record. Please try again.';
        } else if (err.message.includes('413') || err.message.includes('Request Entity Too Large')) {
          // Handle 413 Payload Too Large error
          errorMessage = 'The file is too large. Please upload a file smaller than 10MB.';
        } else if (err.message.includes('400') || err.message.includes('Bad Request')) {
          // Handle 400 Bad Request which might be due to file size limits
          errorMessage = 'The file could not be uploaded. It may exceed the maximum allowed size of 10MB.';
        } else if (err.message.toLowerCase().includes('size') || err.message.toLowerCase().includes('large')) {
          // Catch any other size-related error messages
          errorMessage = 'The file size exceeds the maximum allowed limit of 10MB. Please upload a smaller file.';
        } else {
          errorMessage = `Failed to upload file: ${err.message}`;
        }
      }
      
      // Set error on the specific upload card instead of global error
      setUploadErrors(prev => ({ ...prev, [personId]: errorMessage }));
    } finally {
      setUploadLoading(prev => ({ ...prev, [personId]: false }));
    }
  };
  
  const handleRemoveFile = async (personId: string) => {
    if (!agreement_id || !personId) return;
    
    try {
      setUploadLoading(prev => ({ ...prev, [personId]: true }));
      
      // Delete the file
      await deleteIdFile(agreement_id, personId);
      
      // Update the idFiles state
      setIdFiles(prev => {
        const newState = { ...prev };
        delete newState[personId];
        return newState;
      });
      
      // Update the idFilesResponseData state
      setIdFilesResponseData(prev => {
        const updatedFiles = { ...(prev.files || {}) };
        const updatedFilesData = { ...(prev.filesData || {}) };
        
        // Remove the file data for this person
        if (updatedFiles[personId]) delete updatedFiles[personId];
        if (updatedFilesData[personId]) delete updatedFilesData[personId];
        
        return {
          files: updatedFiles,
          filesData: updatedFilesData
        };
      });
      
      // ADDED: Clear the presigned URL from state to prevent showing cached images
      setPresignedUrls(prev => {
        const newState = { ...prev };
        delete newState[personId];
        return newState;
      });
      
      // Clear any global error
      setError(null);
      
      // Clear any specific upload error for this person
      setUploadErrors(prev => {
        const newState = { ...prev };
        delete newState[personId];
        return newState;
      });
    } catch (err) {
      console.error('Error removing file:', err);
      setError('Failed to remove file. Please try again.');
    } finally {
      setUploadLoading(prev => ({ ...prev, [personId]: false }));
    }
  };
  
  const handleCancelPreview = (personId: string) => {
    // Clear the file preview
    setFilePreview(prev => {
      const newState = { ...prev };
      delete newState[personId];
      return newState;
    });
    
    // Also clear any specific upload error for this person
    setUploadErrors(prev => {
      const newState = { ...prev };
      delete newState[personId];
      return newState;
    });
  };
  
  // Function to fetch a presigned URL for a specific person ID
  const fetchPresignedUrl = async (agreementId: string, personId: string) => {
    if (!agreementId || !personId) return;
    
    try {
      console.log(`Fetching presigned URL for person: ${personId}`);
      
      // Call the getPresignedUrl function from the service
      const url = await getPresignedUrl(agreementId, personId);
      
      // Update the presignedUrls state with the new URL
      setPresignedUrls(prev => ({
        ...prev,
        [personId]: url
      }));
      
      console.log(`Received presigned URL for person ${personId}:`, url);
    } catch (err) {
      console.error(`Error fetching presigned URL for person ${personId}:`, err);
    } finally {
      // Mark this person ID as no longer loading a presigned URL
      setPresignedUrlLoading(prev => ({
        ...prev,
        [personId]: false
      }));
    }
  };

  // File Upload Area Component
  interface FileUploadAreaProps {
    personId: string;
    isRequired?: boolean;
    isUploaded: boolean;
    uploadedFileName: string;
    filePreview?: { data: string; type: string; name: string };
    isLoading?: boolean;
    error?: string;
    disabled?: boolean;
    onFileSelect: (file: File) => void;
    onUpload: () => void;
    onRemove: () => void;
    onCancelPreview: () => void;
  }

  const FileUploadArea: React.FC<FileUploadAreaProps> = ({
    personId,
    isRequired = false,
    isUploaded,
    uploadedFileName,
    filePreview,
    isLoading = false,
    error,
    disabled = false,
    onFileSelect,
    onUpload,
    onRemove,
    onCancelPreview
  }) => {
    const fileInputRef = useRef<HTMLInputElement>(null);
    const [isDragging, setIsDragging] = useState(false);
    
    const handleDragOver = (e: DragEvent<HTMLDivElement>) => {
      e.preventDefault();
      if (!disabled) {
        setIsDragging(true);
      }
    };
    
    const handleDragLeave = (e: DragEvent<HTMLDivElement>) => {
      e.preventDefault();
      if (!disabled) {
        setIsDragging(false);
      }
    };
    
    const handleDrop = (e: DragEvent<HTMLDivElement>) => {
      e.preventDefault();
      if (!disabled) {
        setIsDragging(false);
        
        if (e.dataTransfer.files && e.dataTransfer.files.length > 0) {
          onFileSelect(e.dataTransfer.files[0]);
        }
      }
    };
    
    const handleFileInputChange = (e: ChangeEvent<HTMLInputElement>) => {
      if (!disabled && e.target.files && e.target.files.length > 0) {
        onFileSelect(e.target.files[0]);
      }
    };
    
    const handleClick = () => {
      if (!disabled && fileInputRef.current) {
        fileInputRef.current.click();
      }
    };
    
    // Render different states
    if (isUploaded) {
      // Already uploaded file
      console.log('Rendering uploaded file for personId:', personId);
      
      // Find the file data in the response data object
      const fileData = idFilesResponseData.files?.[personId];
      const fileMetadata = idFilesResponseData.filesData?.[personId];
      console.log('Found file data in response object:', fileData);
      console.log('Found file metadata in response object:', fileMetadata);
      
      // Get file type from the data
      let fileType = '';
      if (fileMetadata && typeof fileMetadata === 'object' && fileMetadata.id_type) {
        fileType = fileMetadata.id_type.toLowerCase();
      } else if (idFiles[personId] && typeof idFiles[personId] === 'object') {
        fileType = (idFiles[personId] as {id_type: string}).id_type.toLowerCase();
      }
      
      // Get presigned URL from response data
      // First try to get it from the filesData.url property
      let presignedUrl = fileMetadata?.url || '';
      
      // If we don't have a presigned URL but we have an ID file, we need to fetch it
      if (!presignedUrl && agreement_id && personId) {
        console.log('No presigned URL found, need to fetch it for:', personId);
        
        // We'll use the ID file path to display a placeholder for now
        // and trigger a fetch for the presigned URL
        const idFilePath = typeof fileData === 'string' ? fileData : fileMetadata?.id_url;
        
        if (idFilePath) {
          // Extract file type from the path
          const fileExtMatch = idFilePath.match(/\.([^.]+)$/);
          if (fileExtMatch && fileExtMatch[1]) {
            fileType = fileExtMatch[1].toLowerCase();
          }
          
          // Check if we already have a presigned URL in state
          if (presignedUrls[personId]) {
            presignedUrl = presignedUrls[personId];
            console.log('Using cached presigned URL for:', personId, presignedUrl);
          } else if (!presignedUrlLoading[personId]) {
            // If we don't have a presigned URL and we're not already loading one, fetch it
            console.log('Fetching presigned URL for:', personId);
            
            // Mark this person ID as loading a presigned URL
            setPresignedUrlLoading(prev => ({ ...prev, [personId]: true }));
            
            // Fetch the presigned URL
            fetchPresignedUrl(agreement_id, personId);
            
            // For now, use the ID file path as a placeholder
            presignedUrl = idFilePath;
          } else {
            // If we're already loading a presigned URL, use the ID file path as a placeholder
            presignedUrl = idFilePath;
          }
        }
      }
      
      console.log('File type:', fileType);
      console.log('Presigned URL from response data:', presignedUrl);
      console.log('ID URL from file data:', fileData?.id_url);
      
      const isImage = fileType === 'jpg' || fileType === 'jpeg' || fileType === 'png';
      
      return (
        <div className="border-2 border-green-100 bg-green-50 rounded-lg p-6 text-center">
          <div className="flex flex-col items-center">
            {/* Show thumbnail for images, icon for PDFs */}
            {isImage ? (
              <div className="w-24 h-24 mb-3 border border-gray-200 rounded overflow-hidden relative">
                {presignedUrlLoading[personId] && (
                  <div className="absolute inset-0 bg-gray-100 bg-opacity-75 flex items-center justify-center z-10">
                    <svg className="animate-spin h-5 w-5 text-primary-500" xmlns="http://www.w3.org/2000/svg" fill="none" viewBox="0 0 24 24">
                      <circle className="opacity-25" cx="12" cy="12" r="10" stroke="currentColor" strokeWidth="4"></circle>
                      <path className="opacity-75" fill="currentColor" d="M4 12a8 8 0 018-8V0C5.373 0 0 5.373 0 12h4zm2 5.291A7.962 7.962 0 014 12H0c0 3.042 1.135 5.824 3 7.938l3-2.647z"></path>
                    </svg>
                  </div>
                )}
                <img
                  src={presignedUrls[personId] || presignedUrl}
                  alt="ID Document"
                  className="w-full h-full object-cover"
                  key={Date.now()} /* Use key prop for cache busting instead of URL modification */
                  onError={(e) => {
                    // Fallback to icon if image fails to load
                    e.currentTarget.style.display = 'none';
                    const parent = e.currentTarget.parentElement;
                    if (parent) {
                      const fallback = document.createElement('div');
                      fallback.className = 'w-full h-full bg-gray-100 flex items-center justify-center';
                      fallback.innerHTML = '<svg xmlns="http://www.w3.org/2000/svg" width="24" height="24" viewBox="0 0 24 24" fill="none" stroke="currentColor" stroke-width="2" stroke-linecap="round" stroke-linejoin="round" class="text-gray-400"><rect x="2" y="2" width="20" height="20" rx="2" ry="2"></rect><circle cx="8.5" cy="8.5" r="1.5"></circle><polyline points="21 15 16 10 5 21"></polyline></svg>';
                      parent.appendChild(fallback);
                    }
                  }}
                />
              </div>
            ) : (
              <div className="w-16 h-16 bg-green-100 rounded-lg flex items-center justify-center mb-3">
                {fileType === 'pdf' ? (
                  <FileText className="h-8 w-8 text-green-600" />
                ) : (
                  <ImageIcon className="h-8 w-8 text-green-600" />
                )}
              </div>
            )}
            
            <p className="text-sm text-gray-600 mb-1">ID file uploaded</p>
            <p className="text-xs text-gray-500 mb-2">
              {typeof uploadedFileName === 'string' ? uploadedFileName.split('/').pop() || '' : ''}
            </p>
            {/* Display file type if available */}
            {idFiles[personId] && typeof idFiles[personId] === 'object' && (
              <p className="text-xs text-gray-500 mb-2">
                Type: {(idFiles[personId] as {id_type: string}).id_type.toUpperCase()}
              </p>
            )}
            {error && (
              <p className="text-xs text-red-600 font-medium mb-2">
                {error}
              </p>
            )}
            {!disabled && (
              <button
                className="text-xs text-red-600 font-medium hover:text-red-800"
                onClick={onRemove}
                disabled={isLoading}
              >
                {isLoading ? (
                  <span className="flex items-center">
                    <svg className="animate-spin -ml-1 mr-2 h-3 w-3 text-red-600" xmlns="http://www.w3.org/2000/svg" fill="none" viewBox="0 0 24 24">
                      <circle className="opacity-25" cx="12" cy="12" r="10" stroke="currentColor" strokeWidth="4"></circle>
                      <path className="opacity-75" fill="currentColor" d="M4 12a8 8 0 018-8V0C5.373 0 0 5.373 0 12h4zm2 5.291A7.962 7.962 0 014 12H0c0 3.042 1.135 5.824 3 7.938l3-2.647z"></path>
                    </svg>
                    Removing...
                  </span>
                ) : 'Remove file'}
              </button>
            )}
            {disabled && (
              <p className="text-xs text-gray-500 font-medium">
                Agreement confirmed - file cannot be modified
              </p>
            )}
          </div>
        </div>
      );
    } else if (filePreview) {
      // File selected but not uploaded yet
      return (
        <div className="border-2 border-primary-100 bg-primary-50 rounded-lg p-6">
          <div className="flex flex-col items-center">
            <div className="w-16 h-16 mb-3">
              {filePreview.type.startsWith('image/') ? (
                <div className="w-16 h-16 relative">
                  <img
                    src={filePreview.data}
                    alt="Preview"
                    className="w-16 h-16 object-cover rounded"
                  />
                </div>
              ) : (
                <div className="w-16 h-16 bg-gray-100 rounded flex items-center justify-center">
                  <FileText className="h-8 w-8 text-gray-500" />
                </div>
              )}
            </div>
            <p className="text-sm font-medium text-gray-800 mb-1">{filePreview.name}</p>
            <p className="text-xs text-gray-500 mb-3">
              {filePreview.type.split('/')[1].toUpperCase()} file
            </p>
            {error && (
              <p className="text-xs text-red-600 font-medium mb-3">
                {error}
              </p>
            )}
            <div className="flex space-x-3">
              <button
                className={`px-3 py-1.5 text-sm rounded transition-colors ${
                  disabled
                    ? 'bg-gray-300 text-gray-500 cursor-not-allowed'
                    : 'bg-primary-500 text-white hover:bg-primary-600'
                }`}
                onClick={disabled ? undefined : onUpload}
                disabled={isLoading || disabled}
              >
                {isLoading ? (
                  <span className="flex items-center">
                    <svg className="animate-spin -ml-1 mr-2 h-4 w-4 text-white" xmlns="http://www.w3.org/2000/svg" fill="none" viewBox="0 0 24 24">
                      <circle className="opacity-25" cx="12" cy="12" r="10" stroke="currentColor" strokeWidth="4"></circle>
                      <path className="opacity-75" fill="currentColor" d="M4 12a8 8 0 018-8V0C5.373 0 0 5.373 0 12h4zm2 5.291A7.962 7.962 0 014 12H0c0 3.042 1.135 5.824 3 7.938l3-2.647z"></path>
                    </svg>
                    Saving & Processing...
                  </span>
                ) : disabled ? 'Upload Disabled' : 'Save & Upload'}
              </button>
              <button
                className="px-3 py-1.5 border border-gray-300 text-gray-700 text-sm rounded hover:bg-gray-50 transition-colors"
                onClick={onCancelPreview}
                disabled={isLoading}
              >
                Cancel
              </button>
            </div>
          </div>
        </div>
      );
    } else {
      // Check if the form is valid to provide appropriate guidance
      const isFormValid = validateForm();
      
      // No file selected
      // Check if this specific person/director has valid fields
      const isPersonValid = () => {
        if (purchaserType === 'personal') {
          const person = localIndividualDetails.find(p => p.id === personId);
          return person && person.firstName && person.lastName && person.email && person.phone &&
                 person.addressLine1 && person.city && person.state && person.postcode;
        } else if (purchaserType === 'smsf') {
          const director = localSMSFDetails.directors.find(d => d.id === personId);
          return director && director.firstName && director.lastName && director.email && director.phone &&
                 director.addressLine1 && director.city && director.state && director.postcode;
        } else if (purchaserType === 'trust') {
          const director = localTrustDetails.directors.find(d => d.id === personId);
          return director && director.firstName && director.lastName && director.email && director.phone &&
                 director.addressLine1 && director.city && director.state && director.postcode;
        } else if (purchaserType === 'company') {
          const director = localCompanyDetails.directors.find(d => d.id === personId);
          return director && director.firstName && director.lastName && director.email && director.phone &&
                 director.addressLine1 && director.city && director.state && director.postcode;
        }
        return false;
      };
      
      const isCurrentPersonValid = isPersonValid();
      const isMissingRequiredId = isRequired && !isUploaded && isCurrentPersonValid;
      
      // No file selected
      return (
        <div
          className={`border-2 border-dashed rounded-lg p-6 text-center transition-colors ${
            disabled ? 'border-gray-200 bg-gray-100 cursor-not-allowed' :
            !isCurrentPersonValid ? 'border-orange-300 bg-orange-50 cursor-default' :
            isMissingRequiredId ? 'border-red-300 bg-red-50 cursor-pointer' :
            isDragging ? 'border-primary-400 bg-primary-50 cursor-pointer' : 'border-gray-300 hover:border-gray-400 cursor-pointer'
          }`}
          onDragOver={!disabled && isCurrentPersonValid ? handleDragOver : undefined}
          onDragLeave={!disabled && isCurrentPersonValid ? handleDragLeave : undefined}
          onDrop={!disabled && isCurrentPersonValid ? handleDrop : undefined}
          onClick={!disabled && isCurrentPersonValid ? handleClick : undefined}
        >
          <input
            type="file"
            ref={fileInputRef}
            className="hidden"
            accept="image/jpeg,image/jpg,image/png,application/pdf"
            onChange={handleFileInputChange}
            disabled={disabled || !isCurrentPersonValid}
          />
          {error && (
            <div className="mb-3">
              <p className="text-xs text-red-600 font-medium">
                {error}
              </p>
            </div>
          )}
          {disabled ? (
            <>
              <div className="w-8 h-8 bg-gray-200 rounded-full flex items-center justify-center mx-auto mb-2">
                <Upload className="w-4 h-4 text-gray-400" />
              </div>
              <p className="text-sm text-gray-600 mb-1">Upload disabled</p>
              <p className="text-xs text-gray-500 mb-1">
                Agreement has been signed - Photo ID uploads are no longer available
              </p>
            </>
          ) : isCurrentPersonValid ? (
            <>
              <Upload className="w-8 h-8 text-gray-400 mx-auto mb-2" />
              <p className="text-sm text-gray-600 mb-1">Click to upload or drag and drop</p>
              <p className="text-xs text-gray-500 mb-2">PNG, JPG, PDF up to 9.5MB</p>
              <p className="text-xs text-gray-500 mb-1">
                All contact details will be saved before uploading.
                This may take a few seconds to process.
              </p>
              {isRequired ? (
                <p className="text-xs text-red-600 font-medium">
                  Required — upload a photo ID to continue
                </p>
              ) : (
                <p className="text-xs text-orange-600 font-medium">
                  Optional — may be requested later by the Account Management Team
                </p>
              )}
            </>
          ) : (
            <>
              <div className="w-8 h-8 bg-orange-100 rounded-full flex items-center justify-center mx-auto mb-2">
                <span className="text-orange-600 font-bold">!</span>
              </div>
              <p className="text-sm text-orange-700 mb-1">Please complete this person's required fields first</p>
              <p className="text-xs text-orange-600 mb-1">
                All contact details for this person must be filled before uploading ID files
              </p>
            </>
          )}
        </div>
      );
    }
  };

  return (
    <div className="bg-white rounded-xl border border-gray-200 p-4 sm:p-6 md:p-8">
      {loading && (
        <div className="mb-4 p-4 bg-blue-50 rounded-lg">
          <p className="text-blue-700 flex items-center">
            <svg className="animate-spin -ml-1 mr-3 h-5 w-5 text-blue-700" xmlns="http://www.w3.org/2000/svg" fill="none" viewBox="0 0 24 24">
              <circle className="opacity-25" cx="12" cy="12" r="10" stroke="currentColor" strokeWidth="4"></circle>
              <path className="opacity-75" fill="currentColor" d="M4 12a8 8 0 018-8V0C5.373 0 0 5.373 0 12h4zm2 5.291A7.962 7.962 0 014 12H0c0 3.042 1.135 5.824 3 7.938l3-2.647z"></path>
            </svg>
            Loading agreement data...
          </p>
        </div>
      )}
      
      {error && (
        <div className="mb-4 p-4 bg-red-50 rounded-lg">
          <p className="text-red-700">{error}</p>
        </div>
      )}
      <div className="mb-6 sm:mb-8">
        <h2 className="text-xl sm:text-2xl font-bold text-gray-900 mb-2">
          Contact Details
        </h2>
        <p className="text-sm sm:text-base text-gray-600">
          Please provide the required contact information.
        </p>
      </div>

      <div className="space-y-6 sm:space-y-8">
        {/* Personal Details Section */}
        {purchaserType === 'personal' && (
          <div className="border border-gray-200 rounded-lg p-4 sm:p-6">
            <div className="flex flex-col sm:flex-row items-start sm:items-center justify-between mb-6 gap-2">
              <h3 className="text-base sm:text-lg font-semibold text-gray-900">
                Personal Details
              </h3>
              <button
                type="button"
                onClick={!isConfirmed ? addPerson : undefined}
                disabled={isConfirmed}
                className={`flex items-center gap-2 px-4 py-2 rounded-full text-sm min-h-[38px] transition-colors ${
                  isConfirmed
                    ? 'bg-gray-300 text-gray-500 cursor-not-allowed'
                    : 'bg-primary-500 text-white hover:bg-primary-600'
                }`}
              >
                <Plus className="w-4 h-4" />
                Add Person
              </button>
            </div>

            {localIndividualDetails.map((details, index) => 
              renderPersonForm(details, index)
            )}
          </div>
        )}

        {/* SMSF Details */}
        {purchaserType === 'smsf' && (
          <div className="border border-gray-200 rounded-lg p-4 sm:p-6">
            <h3 className="text-base sm:text-lg font-semibold text-gray-900 mb-6">SMSF Details</h3>
            
            <div className="grid grid-cols-1 sm:grid-cols-2 gap-4 mb-8">
              <div>
                <label className="block text-sm font-medium text-gray-700 mb-2">
                  Name of SMSF *
                </label>
                <input
                  type="text"
                  value={localSMSFDetails.smsfName}
                  onChange={(e) => setLocalSMSFDetails(prev => ({ ...prev, smsfName: e.target.value }))}
                  className={`w-full px-4 py-3 border border-gray-300 rounded-lg focus:outline-none focus:ring-2 focus:ring-primary-500 min-h-[48px] ${
                    isConfirmed ? 'bg-gray-100 cursor-not-allowed' : ''
                  }`}
                  placeholder="Enter SMSF name"
                  disabled={isConfirmed}
                  readOnly={isConfirmed}
                />
              </div>
              
              <div>
                <label className="block text-sm font-medium text-gray-700 mb-2">
                  Corporate Trustee *
                </label>
                <input
                  type="text"
                  value={localSMSFDetails.corporateTrustee}
                  onChange={(e) => setLocalSMSFDetails(prev => ({ ...prev, corporateTrustee: e.target.value }))}
                  className="w-full px-4 py-3 border border-gray-300 rounded-lg focus:outline-none focus:ring-2 focus:ring-primary-500 min-h-[48px]"
                  placeholder="Enter corporate trustee"
                />
              </div>
              
              
            </div>

            <div>
              <div className="flex flex-col sm:flex-row items-start sm:items-center justify-between mb-6 gap-2">
                <h4 className="text-lg font-semibold text-gray-900">Directors</h4>
                <button
                  type="button"
                  onClick={!isConfirmed ? () => addDirector('smsf') : undefined}
                  disabled={isConfirmed}
                  className={`flex items-center gap-2 px-4 py-2 rounded-full text-sm min-h-[38px] transition-colors ${
                    isConfirmed
                      ? 'bg-gray-300 text-gray-500 cursor-not-allowed'
                      : 'bg-primary-500 text-white hover:bg-primary-600'
                  }`}
                >
                  <Plus className="w-4 h-4" />
                  Add Director
                </button>
              </div>

              {localSMSFDetails.directors.map((director: Director, index: number) =>
                renderDirectorForm(director, 'smsf', index)
              )}
            </div>
          </div>
        )}

        {/* Individual Trust Details */}
        {purchaserType === 'trust' && (
          <div className="border border-gray-200 rounded-lg p-4 sm:p-6">
            <h3 className="text-base sm:text-lg font-semibold text-gray-900 mb-6">Individual Trust Details</h3>
            
            <div className="mb-8">
              <label className="block text-sm font-medium text-gray-700 mb-2">
                Name of Trust *
              </label>
              <input
                type="text"
                value={localTrustDetails.trustName}
                onChange={(e) => setLocalTrustDetails(prev => ({ ...prev, trustName: e.target.value }))}
                className="w-full px-4 py-3 border border-gray-300 rounded-lg focus:outline-none focus:ring-2 focus:ring-primary-500 min-h-[48px]"
                placeholder="Enter trust name"
              />
            </div>

            <div>
              <div className="flex flex-col sm:flex-row items-start sm:items-center justify-between mb-6 gap-2">
                <h4 className="text-lg font-semibold text-gray-900">Directors</h4>
                <button
                  type="button"
                  onClick={!isConfirmed ? () => addDirector('trust') : undefined}
                  disabled={isConfirmed}
                  className={`flex items-center gap-2 px-4 py-2 rounded-full text-sm min-h-[38px] transition-colors ${
                    isConfirmed
                      ? 'bg-gray-300 text-gray-500 cursor-not-allowed'
                      : 'bg-primary-500 text-white hover:bg-primary-600'
                  }`}
                >
                  <Plus className="w-4 h-4" />
                  Add Director
                </button>
              </div>

              {localTrustDetails.directors.map((director: Director, index: number) =>
                renderDirectorForm(director, 'trust', index)
              )}
            </div>
          </div>
        )}

        {/* Company Trust Details */}
        {purchaserType === 'company' && (
          <div className="border border-gray-200 rounded-lg p-4 sm:p-6">
            <h3 className="text-base sm:text-lg font-semibold text-gray-900 mb-6">Company Trust Details</h3>
            
            <div className="grid grid-cols-1 sm:grid-cols-2 gap-4 mb-8">
              <div>
                <label className="block text-sm font-medium text-gray-700 mb-2">
                  Name of Trust *
                </label>
                <input
                  type="text"
                  value={localCompanyDetails.trustName}
                  onChange={(e) => setLocalCompanyDetails(prev => ({ ...prev, trustName: e.target.value }))}
                  className="w-full px-4 py-3 border border-gray-300 rounded-lg focus:outline-none focus:ring-2 focus:ring-primary-500 min-h-[48px]"
                  placeholder="Enter trust name"
                />
              </div>
              
              <div>
                <label className="block text-sm font-medium text-gray-700 mb-2">
                  Corporate Trustee *
                </label>
                <input
                  type="text"
                  value={localCompanyDetails.corporateTrustee}
                  onChange={(e) => setLocalCompanyDetails(prev => ({ ...prev, corporateTrustee: e.target.value }))}
                  className="w-full px-4 py-3 border border-gray-300 rounded-lg focus:outline-none focus:ring-2 focus:ring-primary-500 min-h-[48px]"
                  placeholder="Enter corporate trustee"
                />
              </div>
            </div>

            <div>
              <div className="flex flex-col sm:flex-row items-start sm:items-center justify-between mb-6 gap-2">
                <h4 className="text-lg font-semibold text-gray-900">Directors</h4>
                <button
                  type="button"
                  onClick={!isConfirmed ? () => addDirector('company') : undefined}
                  disabled={isConfirmed}
                  className={`flex items-center gap-2 px-4 py-2 rounded-full text-sm min-h-[38px] transition-colors ${
                    isConfirmed
                      ? 'bg-gray-300 text-gray-500 cursor-not-allowed'
                      : 'bg-primary-500 text-white hover:bg-primary-600'
                  }`}
                >
                  <Plus className="w-4 h-4" />
                  Add Director
                </button>
              </div>

              {localCompanyDetails.directors.map((director: Director, index: number) =>
                renderDirectorForm(director, 'company', index)
              )}
            </div>
          </div>
        )}
      </div>

      <div className="flex flex-col sm:flex-row justify-between items-center gap-4 mt-6 sm:mt-8">
        <button
          onClick={onBack}
          className="w-full sm:w-auto px-5 py-2.5 border border-gray-300 rounded-full font-semibold text-gray-700 hover:bg-gray-50 transition-all duration-200 min-h-[42px] order-2 sm:order-1"
        >
          Back
        </button>
        <div className="text-xs sm:text-sm text-gray-500 order-3 sm:order-2">
          Step 3 of 4
        </div>
        <button
          onClick={handleNext}
          disabled={!validateForm()}
          className={`w-full sm:w-auto px-6 py-2.5 rounded-full font-semibold transition-all duration-200 min-h-[42px] order-1 sm:order-3 ${
            validateForm()
              ? 'bg-primary-500 hover:bg-primary-600 text-white'
              : 'bg-gray-300 text-gray-500 cursor-not-allowed'
          }`}
        >
          Continue
        </button>
      </div>
    </div>
  );
}